// Client events (E01-S4-T1; contracts 1.0 section 7). What a shell instance sends on its one socket.
// Every event carries `cid` for de-duplication (D-55). An utterance is text: speech arrives as a
// transcript on the audio socket (D-35), so there is no audio field here. SurfaceAction is closed:
// there is no kind that approves, resolves, commits or submits, and an event carrying one fails.
import { z } from "zod";
import { IntentEvent, ScreenContextFrame } from "../frame/index.js";
import { Count, Id, NonEmpty, strictObject, version } from "../primitives.js";
import { DriftReport } from "../record.js";
import { ScopedLoopId } from "../sop.js";
export const Posture = z.enum(["orb", "hud", "companion", "container"]);
export const Shell = z.enum([
    "extension-sidepanel",
    "extension-overlay",
    "extension-pip",
    "desktop",
    "web",
    "sim",
]);
export const ViewCapabilities = strictObject({
    voice: z.boolean(),
    pointing: z.boolean(),
    navigate: z.boolean(),
});
/** Every client event: a client id, for de-duplication (D-55). */
export const Base = strictObject({ cid: NonEmpty });
/** A nudge's id: `<tripwireId>#<n>`, the nth firing of that tripwire this session (D-05). */
export const NUDGE_ID_RE = /^[A-Za-z0-9][A-Za-z0-9._-]*#[1-9]\d*$/;
export const NudgeId = z
    .string()
    .regex(NUDGE_ID_RE, "expected <tripwireId>#<n>, n counting from 1");
/** Does a nudge id name this tripwire? Shared by the client nudge event and the Nudge payload. */
export const nudgeIdNames = (id, tripwireId) => id.slice(0, id.lastIndexOf("#")) === tripwireId;
/** Where navigation may go (D-30, D-31): an app, or a screen whose atlas entry declares `entry`. */
export const NavigateRequest = z.discriminatedUnion("kind", [
    strictObject({ kind: z.literal("app"), appId: Id }),
    strictObject({ kind: z.literal("destination"), appId: Id, screenId: Id }),
]);
/** What the surface may ask for. Closed: nothing here approves, resolves, commits or submits (rule 10). */
export const SurfaceAction = z.discriminatedUnion("kind", [
    strictObject({
        kind: z.literal("walkthrough.start"),
        sopId: Id.optional(),
        flowId: Id.optional(),
    }).refine((a) => a.sopId === undefined || a.flowId === undefined, {
        message: "walkthrough.start names a procedure or a flow, not both",
        path: ["flowId"],
    }),
    strictObject({ kind: z.literal("walkthrough.stop") }),
    strictObject({ kind: z.literal("walkthrough.restart") }),
    strictObject({ kind: z.literal("step.ack") }),
    strictObject({ kind: z.literal("decision.choose"), branchId: Id }),
    strictObject({ kind: z.literal("beat.play"), beatId: Id }),
    strictObject({ kind: z.literal("note.draft") }),
    strictObject({ kind: z.literal("loop.done"), id: ScopedLoopId }),
    strictObject({ kind: z.literal("field.explain"), fieldId: Id }),
    strictObject({ kind: z.literal("screen.explain") }),
    strictObject({ kind: z.literal("escalate.note"), text: NonEmpty }),
    strictObject({
        kind: z.literal("offer.answer"),
        id: NonEmpty,
        answer: z.enum(["confirm", "decline"]),
    }),
]);
const cid = Base.shape.cid;
export const ClientEvent = z.discriminatedUnion("t", [
    strictObject({
        cid,
        t: z.literal("hello"),
        v: version(1),
        // lastSeq counted another socket's events; the server ignores it (ADR-028, D-108).
        resume: strictObject({ sessionId: NonEmpty, resumeToken: NonEmpty, lastSeq: Count }).optional(),
        new: z.boolean().optional(),
        practice: z.boolean().optional(),
    }),
    strictObject({
        cid,
        t: z.literal("view.attach"),
        viewId: NonEmpty,
        shell: Shell,
        posture: Posture,
        capabilities: ViewCapabilities,
    }),
    strictObject({
        cid,
        t: z.literal("view.update"),
        viewId: NonEmpty,
        posture: Posture.optional(),
        capabilities: ViewCapabilities.optional(),
    }).refine((e) => e.posture !== undefined || e.capabilities !== undefined, {
        message: "a view.update changes the posture, the capabilities or both",
        path: ["posture"],
    }),
    strictObject({ cid, t: z.literal("view.detach"), viewId: NonEmpty }),
    strictObject({ cid, t: z.literal("focus"), sourceId: NonEmpty.nullable() }), // null: the user has left the application (D-69)
    strictObject({ cid, t: z.literal("frame"), frame: ScreenContextFrame }),
    strictObject({ cid, t: z.literal("intent"), intent: IntentEvent }),
    strictObject({
        cid,
        t: z.literal("nudge"),
        id: NudgeId,
        tripwireId: Id,
        frameSeq: Count,
        shown: z.boolean(),
    }).refine((e) => nudgeIdNames(e.id, e.tripwireId), {
        message: "a nudge id is <tripwireId>#<n> for its own tripwire",
        path: ["id"],
    }),
    strictObject({ cid, t: z.literal("utterance"), id: NonEmpty, viewId: NonEmpty, text: NonEmpty }),
    strictObject({
        cid,
        t: z.literal("action"),
        id: NonEmpty,
        viewId: NonEmpty,
        action: SurfaceAction,
    }),
    strictObject({ cid, t: z.literal("ack"), nudgeId: NudgeId }),
    strictObject({
        cid,
        t: z.literal("playback"),
        beatId: Id.optional(),
        titleId: Id.optional(),
        event: z.enum(["start", "progress", "end"]),
        pct: z.number().min(0).max(100).optional(),
    }).refine((e) => e.beatId !== undefined || e.titleId !== undefined, {
        message: "a playback event names a beat or a title",
        path: ["beatId"],
    }),
    strictObject({ cid, t: z.literal("interrupt"), messageId: NonEmpty }),
    strictObject({
        cid,
        t: z.literal("navigated"),
        offerId: NonEmpty,
        result: z.enum(["done", "refused", "failed"]),
    }),
    // Content-free (D-101): ids and a count, never the value that matched.
    strictObject({
        cid,
        t: z.literal("alert"),
        kind: z.enum(["backstop-hit", "policy"]),
        screenId: Id,
        fieldId: Id.optional(),
        count: z.number().int().positive(),
    }),
    strictObject({ cid, t: z.literal("drift"), report: DriftReport }),
    strictObject({ cid, t: z.literal("pause"), paused: z.boolean() }),
    strictObject({ cid, t: z.literal("resync") }),
    strictObject({ cid, t: z.literal("reauth"), ticket: NonEmpty }),
    strictObject({ cid, t: z.literal("ping") }),
]);
//# sourceMappingURL=client.js.map